import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const POPOVER_H = 340;

const toISODate = (d) => format(d, "yyyy-MM-dd");

const parseValue = (value) => {
  if (!value) return null;
  try {
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
  } catch {
    return null;
  }
};

/**
 * Modern popover date field.
 * value / onChange use YYYY-MM-DD strings (or "").
 */
const DateField = ({
  label,
  value,
  onChange,
  min,
  placeholder = "Select date",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseValue(value), [value]);
  const minDate = useMemo(
    () => parseValue(min) || startOfDay(new Date()),
    [min]
  );
  const [view, setView] = useState(() => selected || minDate);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 280,
    openUp: false,
  });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const wasOpen = useRef(false);

  // Only reset the month view when the popover opens — not on every parent re-render.
  useEffect(() => {
    if (open && !wasOpen.current) {
      setView(selected || minDate);
    }
    wasOpen.current = open;
  }, [open, selected, minDate]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const place = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - 24);
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < POPOVER_H && rect.top > spaceBelow;
      let top = openUp ? rect.top - POPOVER_H - 8 : rect.bottom + 8;
      top = Math.max(8, Math.min(top, window.innerHeight - POPOVER_H - 8));
      let left = rect.left;
      left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
      setCoords({ top, left, width, openUp });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(view));
    const end = endOfWeek(endOfMonth(view));
    return eachDayOfInterval({ start, end });
  }, [view]);

  const display = selected ? format(selected, "dd MMM yyyy") : placeholder;

  const pick = (day) => {
    if (isBefore(day, minDate)) return;
    onChange?.(toISODate(day));
    setOpen(false);
  };

  const calendar = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: coords.openUp ? 6 : -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: coords.openUp ? 4 : -4, scale: 0.98 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            width: coords.width,
            zIndex: 200,
          }}
          className="date-field-popover rounded-2xl border overflow-hidden"
        >
          <div className="date-field-glow pointer-events-none absolute inset-0" />
          <div className="relative p-3.5">
            <div className="flex items-center justify-between mb-3 px-0.5">
              <button
                type="button"
                onClick={() => setView((v) => addMonths(v, -1))}
                className="date-field-nav h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors"
                aria-label="Previous month"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="date-field-month font-display font-semibold text-sm tracking-tight">
                {format(view, "MMMM yyyy")}
              </div>
              <button
                type="button"
                onClick={() => setView((v) => addMonths(v, 1))}
                className="date-field-nav h-8 w-8 rounded-full inline-flex items-center justify-center transition-colors"
                aria-label="Next month"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="date-field-weekday h-8 flex items-center justify-center text-[0.65rem] font-semibold uppercase tracking-wider"
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {days.map((day) => {
                const inMonth = isSameMonth(day, view);
                const isSelected = selected && isSameDay(day, selected);
                const isToday = isSameDay(day, startOfDay(new Date()));
                const disabledDay = isBefore(day, minDate);
                return (
                  <button
                    key={toISODate(day)}
                    type="button"
                    disabled={disabledDay}
                    onClick={() => pick(day)}
                    className={`date-field-day h-9 rounded-lg text-sm font-medium transition-colors ${
                      isSelected
                        ? "is-selected"
                        : disabledDay
                          ? "is-disabled"
                          : inMonth
                            ? "is-in-month"
                            : "is-out-month"
                    } ${isToday && !isSelected ? "is-today" : ""}`}
                  >
                    {format(day, "d")}
                  </button>
                );
              })}
            </div>

            <div className="date-field-footer mt-3 pt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => {
                  const today = startOfDay(new Date());
                  if (isBefore(today, minDate)) return;
                  onChange?.(toISODate(today));
                  setOpen(false);
                }}
                className="date-field-today text-xs font-semibold transition-colors px-2 py-1"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange?.("");
                  setOpen(false);
                }}
                className="date-field-clear text-xs transition-colors px-2 py-1"
              >
                Clear
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="relative">
      {label && (
        <label className="date-field-label block text-xs mb-1.5">{label}</label>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`date-field-trigger w-full rounded-xl border text-left px-4 py-3 text-sm transition-colors flex items-center gap-3 disabled:opacity-50 ${
          open ? "is-open" : ""
        } ${selected ? "has-value" : ""}`}
      >
        <span
          className={`date-field-icon h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
            selected ? "has-value" : ""
          }`}
        >
          <CalendarIcon size={15} />
        </span>
        <span className="min-w-0 flex-1 truncate font-medium">{display}</span>
      </button>

      {typeof document !== "undefined"
        ? createPortal(calendar, document.body)
        : null}
    </div>
  );
};

export default DateField;
